import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { makeStyles } from 'tss-react/mui'
import { FilterGenerator } from './FilterGenerator'
import { Filter } from '../ApiView'
import { InternalFilter, InternalStringFilter } from '../ApiViewModels'

const useStyles = makeStyles()(theme => ({
  filterWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
  },
}))

/**
 * It sets the filter string for its parent component, so that the url with filters can be sent.
 */
export interface FiltersProps<T> {
  filters: Filter<T>[]
  setFilterString: Dispatch<SetStateAction<string>>
  setInternalFilters?: Dispatch<SetStateAction<InternalFilter[]>>
}

// sets filter string used by fetch api and internalFilter values (in case we need to fetch all data and filter it in frontend)
const Filters = <T,>({ filters, setFilterString, setInternalFilters }: FiltersProps<T>): React.ReactElement => {
  const { classes } = useStyles()
  const [stringifiedFilters, setStringifiedFilters] = useState<string[]>(filters.map(_ => ''))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filterValues, setFilterValues] = useState<any[]>(filters.map(_ => undefined))

  useEffect(() => {
    setFilterString(stringifiedFilters.reduce((prev, curr) => (curr ? `${prev ? prev + ',' : ''}${curr}` : prev), ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifiedFilters])

  return (
    <div className={classes.filterWrapper}>
      {filters.map((filter, index) => (
        <FilterGenerator
          setFilterString={value => {
            if (filter.type.type === 'internal') {
              setInternalFilters?.(filters => [
                ...filters.filter(filterToFilter => filterToFilter.name !== filter.name),
                { name: filter.name as string, filterFunction: (filter.type as InternalStringFilter).filterFunction, value },
              ])
            } else {
              setStringifiedFilters(filters =>
                filters?.map((filterToFind, jindex) => {
                  if (index === jindex) return value
                  return filterToFind
                })
              )
            }
          }}
          setFilterValue={value => setFilterValues(values => values.map((valueToMap, jindex) => (jindex === index ? value : valueToMap)))}
          variant="outlined"
          label={filter.label}
          name={filter.name as string}
          filterType={{ ...filter.type, ...(filterValues[index] !== undefined && { value: filterValues[index] }) }}
          customOnChange={filter.customOnChange}
          className={filter.className}
          key={index}
        />
      ))}
    </div>
  )
}

export default Filters
